import React, { Component } from 'react';
import moment from 'moment';
import { flowRight as compose } from 'lodash';
import { inject, observer } from 'mobx-react';
import { Container, Grid, Button, Box, CircularProgress, TextField } from '@mui/material';

import AxlDialog from 'components/AxlDialog';

import acl from 'constants/acl';
import { validateField } from 'constants/validationSchemas';
import { BANK_REQUEST_STATUS } from 'constants/colors';

import styles from './styles';

class Account extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showBankConfirmation: false,
    };
  }

  validates = {
    company: ['notEmpty'],
    email: ['notEmpty', 'email'],
    contact_name: ['notEmpty'],
    phone_number: ['notEmpty', 'phone'],
    bank_routing: ['notEmpty', 'number'],
    bank_account: ['notEmpty', 'number'],
  };

  componentDidMount() {
    const { store } = this.props;
    store.userStore.getLatestBankRequest();
  }

  onTextFieldChange = (field) => (event) => {
    const { store } = this.props;

    store.userStore.updateDSPData(field, event.target.value);
    const fieldValidates = this.validates[field];

    if (fieldValidates) {
      this.checkError(field);
    }
  };

  accountInfoFields = ['company', 'phone_number', 'email', 'contact_name'];
  bankInfoFields = ['bank_routing', 'bank_account'];

  checkError = (field, prefix) => {
    const { store } = this.props;
    const { accountData } = store.userStore;
    if (!prefix) {
      const initErrorField = 'init_' + field + '_error';
      this.setState({ [initErrorField]: undefined });
    }

    const errorField = prefix ? prefix + field + '_error' : field + '_error';

    const validationRules = this.validates[field];

    if (!validationRules) return;
    const value = accountData[field];

    const result = validateField(field, value, validationRules);

    if (!result) this.setState({ [errorField]: undefined });
    else {
      this.setState({ [errorField]: result });
    }
  };

  save = () => {
    const { store } = this.props;
    store.userStore.updateDsp();
  };

  submitBankRequest = () => {
    const { store } = this.props;
    store.userStore.submitBankInfoRequest((res) => {
      if (res.ok) {
        this.setState({ showBankConfirmation: true });
      }
    });
  };

  render() {
    const { showBankConfirmation } = this.state;
    const { store } = this.props;
    const { userStore, permissionStore } = store;
    const { updating, getAccountDataValue, bankRequest } = userStore;

    const hasAccountError = this.accountInfoFields.find((field) => !!this.state[field + '_error'] || !!this.state['init_' + field + '_error']);
    const hasBankError = this.bankInfoFields.find((field) => !!this.state[field + '_error'] || !!this.state['init_' + field + '_error']);

    const isDeniedUpdateAccountInfo = permissionStore.isDenied(acl.PROFILE.ACCOUNT_INFO);
    const isDeniedUpdateBankInfo = permissionStore.isDenied(acl.PROFILE.BANK_INFO);

    return (
      <Container>
        <h3>Account Settings</h3>
        <Box style={{ position: 'relative' }}>
          {updating && (
            <Box sx={styles.overlay}>
              <CircularProgress color="primary" size={70} thickness={2} />
            </Box>
          )}
          <Box sx={styles.root}>
            <Box component="h3" my={1}>
              Account Information
            </Box>
            <Grid container spacing={4}>
              <Grid size={12}>
                <Box sx={styles.fieldLabel}>COMPANY</Box>
                <TextField
                  onChange={this.onTextFieldChange('company')}
                  value={getAccountDataValue('company') || ''}
                  error={!!this.state['company_error']}
                  helperText={this.state['company_error']}
                  disabled={isDeniedUpdateAccountInfo}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid size={6}>
                <Box sx={styles.fieldLabel}>NAME OF CONTACT</Box>
                <TextField
                  onChange={this.onTextFieldChange('contact_name')}
                  value={getAccountDataValue('contact_name') || ''}
                  error={!!this.state['contact_name_error']}
                  helperText={this.state['contact_name_error']}
                  disabled={isDeniedUpdateAccountInfo}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid size={6}>
                <Box sx={styles.fieldLabel}>CONTACT PHONE NUMBER</Box>
                <TextField
                  onChange={this.onTextFieldChange('phone_number')}
                  value={getAccountDataValue('phone_number') || ''}
                  error={!!this.state['phone_number_error']}
                  helperText={this.state['phone_number_error']}
                  disabled={isDeniedUpdateAccountInfo}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid size={12}>
                <Box sx={styles.fieldLabel}>CONTACT EMAIL</Box>
                <TextField
                  onChange={this.onTextFieldChange('email')}
                  value={getAccountDataValue('email') || ''}
                  error={!!this.state['email_error']}
                  helperText={this.state['email_error']}
                  disabled={isDeniedUpdateAccountInfo}
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>
            <Box textAlign="right" mt={2}>
              {!isDeniedUpdateAccountInfo && (
                <Button
                  disabled={!!hasAccountError || updating}
                  onClick={this.save}
                  size="small"
                  disableElevation
                  variant="contained"
                  color="primary"
                >
                  Save
                </Button>
              )}
            </Box>
          </Box>

          <Box sx={styles.root} mt={3}>
            <Box component="h3" my={1}>
              Bank Information
            </Box>
            <Grid container spacing={4}>
              <Grid size={6}>
                <Box sx={styles.fieldLabel}>BANK ACCOUNT</Box>
                <TextField
                  onChange={this.onTextFieldChange('bank_account')}
                  value={getAccountDataValue('bank_account') || ''}
                  error={!!this.state['bank_account_error']}
                  helperText={this.state['bank_account_error']}
                  disabled={isDeniedUpdateBankInfo}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid size={6}>
                <Box sx={styles.fieldLabel}>BANK ROUTING</Box>
                <TextField
                  onChange={this.onTextFieldChange('bank_routing')}
                  value={getAccountDataValue('bank_routing') || ''}
                  error={!!this.state['bank_routing_error']}
                  helperText={this.state['bank_routing_error']}
                  disabled={isDeniedUpdateBankInfo}
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>
            {bankRequest?.id && (
              <Box mt={2}>
                <Box my={1} display="flex">
                  <Box sx={styles.flexItem}>Status:</Box>
                  <Box sx={styles.flexItem}>
                    <Box component="strong" style={{ color: BANK_REQUEST_STATUS[bankRequest?.status] }}>
                      {bankRequest?.status}
                    </Box>
                  </Box>
                  <Box sx={styles.flexItem}>at</Box>
                  <Box sx={styles.flexItem}>
                    <em>
                      {moment(bankRequest?.status === 'SUBMITTED' ? bankRequest?._updated : bankRequest?.approved_date).format('YYYY-MM-DD HH:mm')}
                    </em>
                  </Box>
                </Box>
                {bankRequest?.status === 'SUBMITTED' && (
                  <Box style={{ padding: 8, backgroundColor: '#f2f1ff', width: 330 }}>
                    <Grid container spacing={4}>
                      <Grid>
                        <Box>
                          <strong>Bank Account</strong>
                        </Box>
                        <Box>{bankRequest?.new_account_number}</Box>
                      </Grid>
                      <Grid>
                        <Box>
                          <strong>Bank Routing</strong>
                        </Box>
                        <Box>{bankRequest?.new_routing_number}</Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>
            )}
            <Box textAlign="right" mt={2}>
              {!isDeniedUpdateBankInfo && (
                <Button
                  disabled={!!hasBankError || updating}
                  onClick={this.submitBankRequest}
                  size="small"
                  disableElevation
                  variant="contained"
                  color="primary"
                >
                  Submit
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        <AxlDialog
          isOpen={showBankConfirmation}
          handleClose={() => this.setState({ showBankConfirmation: false })}
          maxWidth="sm"
          alignTitle="center"
          childrenTitle={<Box fontWeight={700}>Bank Info Update Confirmation</Box>}
        >
          <Box p={2} textAlign="center" lineHeight={1.5}>
            Thank you for submitting the request. We will review the bank update request within 1-3 working days and will reach out to you directly
            with any questions.
          </Box>
        </AxlDialog>
      </Container>
    );
  }
}

export default compose(inject('store'), observer)(Account);
